import React, { useContext, useRef } from 'react';
import { useDrag } from 'react-dnd';
import { ColumnProps } from 'antd/lib/table';

import CustomersContext from '@totem/components/orgManagement/orgDetails/netSuiteKeys/options/customersContext';
import { NetSuiteCustomer } from '@totem/components/orgManagement/orgDetails/netSuiteKeys/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import { isNotNull } from '@totem/utilities/common';
import {
  sortBooleanAscending,
  sortStringAscending,
} from '@totem/utilities/tableUtilities';

const CustomerTable = () => {
  const { netSuiteCustomers, pageSize } = useContext(CustomersContext);
  const DND_ITEM_TYPE = 'NETSUITE_CUSTOMER';

  type DragItem = {
    netSuiteCustomerKey: string;
  };

  type DragRowProps = React.HTMLAttributes<HTMLTableRowElement> & {
    index?: number;
    record?: NetSuiteCustomer;
  };

  const DraggableCustomerRow = ({
    index,
    record,
    className,
    style,
    ...restProps
  }: DragRowProps) => {
    const ref = useRef<HTMLTableRowElement>(null);

    const [{ isDragging }, drag] = useDrag({
      type: DND_ITEM_TYPE,
      item: { netSuiteCustomerKey: record?.netSuiteKey } as DragItem,
      canDrag: !!record?.netSuiteKey,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    });

    drag(ref);

    return (
      <tr
        {...restProps}
        ref={ref}
        className={className}
        style={{
          cursor: 'grab',
          opacity: isDragging ? 0.4 : 1,
          ...style,
        }}
        data-row-index={index}
      />
    );
  };

  const columns: ColumnProps<NetSuiteCustomer>[] = [
    {
      title: 'NetSuite Key',
      dataIndex: 'netSuiteKey',
      key: 'netSuiteKey',
      render: (_, record: NetSuiteCustomer) => (
        <span>{record.netSuiteKey}</span>
      ),
      defaultSortOrder: 'ascend',
      sorter: (compA, compB) =>
        sortStringAscending(compA.netSuiteKey, compB.netSuiteKey),
    },
    {
      title: 'Company',
      dataIndex: 'company',
      key: 'company',
      render: (_, record: NetSuiteCustomer) => <span>{record.company}</span>,
      defaultSortOrder: 'ascend',
      sorter: (compA, compB) =>
        sortStringAscending(compA.company, compB.company),
    },
    {
      title: 'Is Person',
      dataIndex: 'isPerson',
      key: 'isPerson',
      render: (_, record: NetSuiteCustomer) => (
        <span>{record.isPerson ? 'True' : 'False'}</span>
      ),
      defaultSortOrder: 'ascend',
      sorter: (compA, compB) =>
        sortBooleanAscending(compA.isPerson, compB.isPerson),
    },
  ];

  return (
    <>
      <IBTable
        showSorterTooltip
        columns={columns}
        dataSource={isNotNull(netSuiteCustomers) ? netSuiteCustomers : []}
        loading={false}
        rowKey={(record) => record.netSuiteKey}
        components={{
          body: {
            row: DraggableCustomerRow,
          },
        }}
        onRow={(record, index) =>
          ({
            index,
            record,
          }) as React.HTMLAttributes<HTMLElement>
        }
        pagination={{
          showSizeChanger: true,
          pageSize,
        }}
      />
    </>
  );
};

export default CustomerTable;
